import type { ChatMessage, ChatThread, ChatUser, MessageChannelGroupId } from '../types/chat'
import {
  BRUNO_DISPLAY_NAME,
  isBrunoConversation,
  isBrunoUserId,
  isPersonalBrunoConversation,
  partitionMentions,
} from '@shared/bruno/constants'
import { resolveBrunoBotUserId } from '../utils/bruno-config'
import { setLastMessageSender } from '../utils/last-message-sender-cache'
import { syncGroupCoverFromServer } from './group-image-service'
import { canCreateGroupChat } from '../utils/rbac'
import { buildGroupTitleFromUsers, canMentionToAddMember } from '../utils/mention-permissions'
import { isDeactivatedUser } from '../utils/deactivated-user'
import {
  encodeReplyBody,
  formatThreadPreview,
  parseReplyBody,
  type ReplyQuote,
} from '../utils/message-reply'
import { encodeGifMessageBody } from '../utils/message-media-display'
import { encodeImagesMessageBody, parseImagesMessageBody } from '../utils/message-images'
import type { UserProfile } from './appsync-client'
import {
  addReaction,
  createConversation,
  fetchMe,
  listConversations,
  listMessagesPage,
  listUsers,
  markConversationRead,
  searchUsers,
  deleteMessage as deleteMessageApi,
  removeReaction,
  sendMessage,
  undeleteMessage as undeleteMessageApi,
  updateConversation,
  updateMessage as updateMessageApi,
  type AttachmentType,
  type Conversation,
  type ConversationType,
  type Message,
} from './appsync-client'
import {
  inferAttachmentType,
  sendMessageWithAttachment,
  uploadAttachmentFile,
  uploadChatMedia,
  uploadGifFromUrl,
} from './media-upload-service'

function parseChatSettingsJson(raw: unknown): Record<string, unknown> | null {
  if (!raw) return null
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as Record<string, unknown>
    } catch {
      return null
    }
  }
  return raw as Record<string, unknown>
}

const TYPE_TO_CHANNEL: Record<ConversationType, MessageChannelGroupId> = {
  DIRECT: 'direct',
  GROUP: 'groups',
  ANNOUNCEMENT: 'announcements',
}

function parseTime(iso?: string | null): number {
  if (!iso) return 0
  const t = Date.parse(iso)
  return Number.isNaN(t) ? 0 : t
}

function normalizeParticipantSet(ids: string[]): Set<string> {
  return new Set(ids.filter(Boolean))
}

/** Full participant set for a group, always including the current user. */
export function buildGroupParticipantSet(
  participantIds: string[],
  currentUserId: string,
): string[] {
  return [...normalizeParticipantSet([currentUserId, ...participantIds])]
}

/** Order-independent exact match of participant user IDs. */
export function participantSetsMatch(a: string[], b: string[]): boolean {
  const setA = normalizeParticipantSet(a)
  const setB = normalizeParticipantSet(b)
  if (setA.size !== setB.size) return false
  for (const id of setA) {
    if (!setB.has(id)) return false
  }
  return true
}

export async function findExistingGroupConversation(
  participantIds: string[],
  excludeConversationId?: string,
): Promise<ChatThread | null> {
  const all = await listConversations(100)
  const groups = all.filter(
    (c) =>
      c.type === 'GROUP' &&
      c.conversationId !== excludeConversationId &&
      participantSetsMatch(c.participantIds ?? [], participantIds),
  )
  return groups[0] ? conversationToThread(groups[0]) : null
}

function conversationToThread(c: Conversation): ChatThread {
  if (c.lastMessageSenderId) {
    setLastMessageSender(c.conversationId, c.lastMessageSenderId)
  }
  // Keep the reactive cover-image cache in step with the server so the avatar
  // shows the persisted group photo (and clears it cross-device when removed).
  if (c.type === 'GROUP') {
    syncGroupCoverFromServer(c.conversationId, c.coverImageUrl ?? null)
  }
  return {
    id: c.conversationId,
    title: c.title?.trim() || 'Conversation',
    lastMessage: formatThreadPreview(c.lastMessagePreview?.trim() || ''),
    lastSentAt: parseTime(c.lastMessageAt ?? c.createdAt),
    unread: c.unread ?? false,
    lastMessageSenderId: c.lastMessageSenderId ?? undefined,
    conversationType: c.type,
    channelGroupId: TYPE_TO_CHANNEL[c.type],
    participantIds: c.participantIds ?? [],
    createdBy: c.createdBy,
    coverImageUrl: c.coverImageUrl ?? null,
    readByOthersAt: c.readByOthersAt ? parseTime(c.readByOthersAt) : undefined,
  }
}

export function messageToChat(m: Message, currentUserId: string): ChatMessage {
  const rawBody = m.body ?? ''
  const { quote: replyTo, text: afterReply } = parseReplyBody(rawBody)
  const { extraUrls, visibleText } = parseImagesMessageBody(afterReply)
  return {
    id: m.messageId,
    threadId: m.conversationId,
    text: visibleText,
    sender: m.senderId === currentUserId ? 'me' : 'other',
    senderId: m.senderId,
    createdAt: parseTime(m.createdAt),
    messageType: m.messageType,
    mentions: m.mentionIds ?? undefined,
    attachmentUrl: m.attachmentUrl,
    attachmentType: m.attachmentType,
    extraAttachmentUrls: extraUrls.length ? extraUrls : undefined,
    reactions: m.reactions?.map((r) => ({ emoji: r.emoji, userIds: r.userIds })),
    replyTo,
    editedAt: m.editedAt ? parseTime(m.editedAt) : undefined,
    isDeleted: Boolean(m.isDeleted),
  }
}

export const GROUP_CREATE_FORBIDDEN_MESSAGE =
  'You are not permitted to create group chats. Please ask an admin-level user to create a group for you.'

export const THREAD_MESSAGE_PAGE_SIZE = 20

let cachedMe: ChatUser | null = null
/**
 * Bumped by every sign-out reset so a `fetchMe` that was in flight for the
 * previous account cannot repopulate the caches after the reset.
 */
let userCacheGeneration = 0

export function getCachedChatUserId(): string | undefined {
  return cachedMe?.userId
}

export function getCachedChatUser(): ChatUser | undefined {
  return cachedMe ?? undefined
}

/**
 * The signed-in user's id, preferring the cached chat profile only when it
 * belongs to `authUserId`. A cache left over from a previous account on this
 * device (sign-out → sign-in without an app restart) must never stand in for
 * the authenticated user.
 */
export function resolveSelfUserId(authUserId: string | null | undefined): string | undefined {
  const cached = cachedMe?.userId
  if (!authUserId) return undefined
  return cached === authUserId ? cached : authUserId
}

/** Whether the cached chat profile belongs to `authUserId`. */
export function isCachedChatUser(authUserId: string | null | undefined): boolean {
  return Boolean(authUserId) && cachedMe?.userId === authUserId
}

/**
 * Drop the in-memory signed-in profile and the roster snapshot. Called from
 * sign-out: these module caches outlive the session (no page reload), and the
 * voice-call controller and call overlays read them synchronously as "me".
 */
export function resetCachedChatUser(): void {
  cachedMe = null
  directoryUsersCache = []
  rememberedDirectoryUsers.clear()
  directoryUsersFetchedAt = 0
  directoryUsersInFlight = null
  userCacheGeneration += 1
}

export async function getCurrentChatUser(): Promise<ChatUser> {
  const generation = userCacheGeneration
  const me = await fetchMe()
  const next: ChatUser = {
    userId: me.userId,
    email: me.email,
    displayName: me.displayName,
    givenName: me.givenName,
    familyName: me.familyName,
    roleType: me.roleType,
    providerLevel: me.providerLevel,
    avatarUrl: me.avatarUrl,
    title: me.title,
    settingsJson: parseChatSettingsJson(me.settingsJson),
  }
  if (generation === userCacheGeneration) cachedMe = next
  return next
}

let directoryUsersCache: ChatUser[] = []
/**
 * People this session reached by exact search and then contacted. Provider
 * Basic directories list only who they work with, and a new thread reaches
 * the server's list a minute or two later, so these stay merged in until then
 * (or sign-out) instead of the new thread showing no name.
 */
const rememberedDirectoryUsers = new Map<string, ChatUser>()

export function getCachedDirectoryUser(userId: string): ChatUser | undefined {
  return directoryUsersCache.find((u) => u.userId === userId)
}

/**
 * The directory is fetched directly (bypassing the directory store) by ~10 call
 * sites — calls, video-calls, announcements, new-group, sessions, on-call,
 * notifications, and the messages page — each on mount. That made every screen
 * visit re-run a full `listUsers` request, a big part of the "reloads on every
 * click" feel. We cache the mapped result for a short window with stale-while-
 * revalidate so revisits serve instantly, and de-dupe concurrent fetches.
 *
 * Directory data is read-mostly (roles/names change rarely) and live presence is
 * layered on top by the presence store, so a short staleness window is safe.
 * Pass `{ force: true }` (used by the directory store's manual reload) to bypass
 * the cache, and call `invalidateDirectoryUsersCache()` after a mutation that
 * changes the roster (e.g. an admin role change).
 */
const DIRECTORY_USERS_TTL_MS = 60_000

let directoryUsersFetchedAt = 0
let directoryUsersInFlight: Promise<ChatUser[]> | null = null

export function invalidateDirectoryUsersCache(): void {
  directoryUsersFetchedAt = 0
}

/** Users per directory page, and how many pages one load may read (10 000 people). */
export const DIRECTORY_PAGE_SIZE = 500
export const DIRECTORY_MAX_PAGES = 20

export type DirectoryUserPage = {
  items: UserProfile[]
  nextCursor: string | null
  hasNextPage: boolean
}

/** One page of the directory; pass the previous page's `nextCursor` (opaque) for the next. */
export async function listDirectoryUserPage(
  cursor?: string | null,
  pageSize = DIRECTORY_PAGE_SIZE,
): Promise<DirectoryUserPage> {
  const offset = cursor ? Number.parseInt(cursor, 10) || 0 : 0
  const items = await listUsers(pageSize, offset)
  const hasNextPage = items.length >= pageSize
  return { items, nextCursor: hasNextPage ? String(offset + items.length) : null, hasNextPage }
}

/** Everyone the caller may see, page by page (the server orders by name). */
async function listAllDirectoryUsers(): Promise<UserProfile[]> {
  const byId = new Map<string, UserProfile>()
  let cursor: string | null = null
  for (let page = 0; page < DIRECTORY_MAX_PAGES; page += 1) {
    const result: DirectoryUserPage = await listDirectoryUserPage(cursor)
    for (const user of result.items) byId.set(user.userId, user)
    if (!result.hasNextPage) break
    cursor = result.nextCursor
  }
  return [...byId.values()]
}

function toDirectoryChatUser(u: UserProfile): ChatUser {
  return {
    userId: u.userId,
    email: u.email,
    displayName: u.displayName,
    givenName: u.givenName,
    familyName: u.familyName,
    roleType: u.roleType,
    providerLevel: u.providerLevel,
    avatarUrl: u.avatarUrl,
    title: u.title,
    settingsJson: parseChatSettingsJson(u.settingsJson),
    presenceStatus: u.presenceStatus ?? null,
    lastActiveAt: u.lastActiveAt ?? null,
    presenceVisible: u.presenceVisible ?? null,
  }
}

async function fetchDirectoryUsers(): Promise<ChatUser[]> {
  const generation = userCacheGeneration
  const users = await listAllDirectoryUsers()
  // Archived accounts are returned for L3+ admin directory; keep them out of
  // messaging / mention / call pickers.
  const mapped = users.filter((u) => u.status !== 'INACTIVE').map(toDirectoryChatUser)
  if (generation === userCacheGeneration) {
    for (const [userId, user] of rememberedDirectoryUsers) {
      if (mapped.some((u) => u.userId === userId)) rememberedDirectoryUsers.delete(userId)
      else mapped.push(user)
    }
    directoryUsersCache = mapped
    directoryUsersFetchedAt = Date.now()
  }
  return mapped
}

function refreshDirectoryUsers(): Promise<ChatUser[]> {
  if (directoryUsersInFlight) return directoryUsersInFlight
  const request = fetchDirectoryUsers().finally(() => {
    if (directoryUsersInFlight === request) directoryUsersInFlight = null
  })
  directoryUsersInFlight = request
  return request
}

export async function listDirectoryUsers(options?: {
  force?: boolean
}): Promise<ChatUser[]> {
  const hasCache = directoryUsersFetchedAt > 0 && directoryUsersCache.length > 0
  const age = Date.now() - directoryUsersFetchedAt

  if (!options?.force && hasCache) {
    // Stale-while-revalidate: serve the cached roster immediately; if it has
    // aged past the TTL, kick a background refresh so the next read is fresh.
    if (age >= DIRECTORY_USERS_TTL_MS) void refreshDirectoryUsers()
    return directoryUsersCache
  }

  return refreshDirectoryUsers()
}

/**
 * Exact lookup by whole email address or whole name (case and spacing ignored).
 * Provider Basic staff reach people outside the ones they work with this way.
 */
export async function searchDirectoryUsers(query: string): Promise<ChatUser[]> {
  const search = query.trim()
  if (!search) return []
  const users = await searchUsers(search)
  return users.filter((u) => u.status !== 'INACTIVE').map(toDirectoryChatUser)
}

/** Changes on sign-out / account switch: take it before an await, pass it to {@link rememberDirectoryUser}. */
export function directoryCacheGeneration(): number {
  return userCacheGeneration
}

/**
 * Keep someone found by exact search in the directory (names, avatars) for
 * this session. Ignored when the account changed since `generation` was taken.
 */
export function rememberDirectoryUser(user: ChatUser, generation: number): void {
  if (generation !== userCacheGeneration) return
  if (directoryUsersCache.some((u) => u.userId === user.userId)) return
  rememberedDirectoryUsers.set(user.userId, user)
  directoryUsersCache = [...directoryUsersCache, user]
}

export async function resolveDirectoryUser(userId: string): Promise<ChatUser | undefined> {
  const cached = getCachedDirectoryUser(userId)
  if (cached?.displayName?.trim()) return cached
  const users = await listDirectoryUsers()
  return users.find((u) => u.userId === userId)
}

export async function listAllThreads(): Promise<ChatThread[]> {
  const all = await listConversations(100)
  return all.map(conversationToThread).sort((a, b) => b.lastSentAt - a.lastSentAt)
}

/**
 * Unified messaging inbox: every DIRECT and GROUP conversation in one list,
 * newest activity first. Excludes ANNOUNCEMENT (wall) channels and the personal
 * Bruno assistant thread, which live on their own surfaces. Each thread keeps
 * its own `channelGroupId`/`conversationType` so callers can still distinguish a
 * 1:1 from a group per-conversation.
 */
export async function listMessageThreads(): Promise<ChatThread[]> {
  const all = await listAllThreads()
  return all.filter((c) => {
    if (c.channelGroupId !== 'direct' && c.channelGroupId !== 'groups') return false
    if (isBrunoConversation(c)) return false
    return true
  })
}

export async function getOrCreateBrunoDirectConversation(
  meId?: string,
): Promise<ChatThread> {
  const me = meId ? { userId: meId } : await fetchMe()
  const brunoId = resolveBrunoBotUserId()
  const all = await listConversations(100)
  const existing = all.find(
    (c) =>
      c.type === 'DIRECT' &&
      c.participantIds.includes(me.userId) &&
      c.participantIds.includes(brunoId) &&
      c.participantIds.length === 2,
  )
  if (existing) return conversationToThread(existing)

  const created = await createConversation({
    type: 'DIRECT',
    title: BRUNO_DISPLAY_NAME,
    participantIds: [brunoId],
  })
  return conversationToThread(created)
}

export async function getOrCreateDirectConversation(
  peerUserId: string,
  currentUserId: string,
): Promise<ChatThread> {
  const all = await listConversations(100)
  const existing = all.find(
    (c) =>
      c.type === 'DIRECT' &&
      c.participantIds.includes(peerUserId) &&
      c.participantIds.includes(currentUserId),
  )
  if (existing) return conversationToThread(existing)

  const created = await createConversation({
    type: 'DIRECT',
    participantIds: [peerUserId],
  })
  return conversationToThread(created)
}

export async function loadThreadMessagesPage(
  conversationId: string,
  currentUserId: string,
  nextToken?: string | null,
): Promise<{ items: ChatMessage[]; nextToken: string | null }> {
  const page = await listMessagesPage(conversationId, {
    limit: THREAD_MESSAGE_PAGE_SIZE,
    nextToken: nextToken ?? undefined,
    sortDirection: 'DESC',
  })
  const items = page.items
    .map((m) => messageToChat(m, currentUserId))
    .sort((a, b) => a.createdAt - b.createdAt)
  return { items, nextToken: page.nextToken ?? null }
}

export function getDirectThreadPeerId(
  thread: ChatThread,
  currentUserId: string,
): string | undefined {
  return (thread.participantIds ?? []).find((id) => id !== currentUserId)
}

export type ThreadAvatarMember = { name: string; avatarUrl?: string | null }

export type ThreadAvatarInfo = {
  name: string
  avatarUrl?: string | null
  isGroup: boolean
  members: ThreadAvatarMember[]
}

export function getGroupMemberAvatars(
  thread: ChatThread,
  currentUserId: string,
  usersById: Map<string, ChatUser>,
  max = 4,
): ThreadAvatarMember[] {
  const ids = [...new Set([...(thread.participantIds ?? []), currentUserId])]
  return ids
    // Deactivated stubs must not appear in the member avatar collage.
    .filter((id) => !isDeactivatedUser(usersById.get(id)))
    .slice(0, max)
    .map((id) => {
      const u = usersById.get(id)
      return { name: u?.displayName ?? 'User', avatarUrl: u?.avatarUrl }
    })
}

export function getThreadAvatar(
  thread: ChatThread,
  channelGroupId: MessageChannelGroupId,
  currentUserId: string,
  usersById: Map<string, ChatUser>,
): ThreadAvatarInfo {
  if (channelGroupId === 'direct') {
    const peerId = getDirectThreadPeerId(thread, currentUserId)
    const peer = peerId ? usersById.get(peerId) : undefined
    const name = peer?.displayName ?? thread.title
    return {
      name,
      avatarUrl: peer?.avatarUrl,
      isGroup: false,
      members: [{ name, avatarUrl: peer?.avatarUrl }],
    }
  }
  const members = getGroupMemberAvatars(thread, currentUserId, usersById)
  return {
    name: getGroupThreadTitle(thread, usersById),
    avatarUrl: null,
    isGroup: true,
    members,
  }
}

export async function sendSystemMessage(
  conversationId: string,
  body: string,
): Promise<ChatMessage> {
  const me = await fetchMe()
  const msg = await sendMessage({
    conversationId,
    body,
    messageType: 'SYSTEM',
  })
  return messageToChat(msg, me.userId)
}

export async function sendChatMessage(
  conversationId: string,
  text: string,
  options?: { mentionIds?: string[]; replyTo?: ReplyQuote },
): Promise<ChatMessage> {
  const me = await fetchMe()
  const trimmed = text.trim()
  const body = options?.replyTo ? encodeReplyBody(options.replyTo, trimmed) : trimmed
  const msg = await sendMessage({
    conversationId,
    body,
    messageType: 'TEXT',
    mentionIds: options?.mentionIds?.length ? options.mentionIds : undefined,
  })
  return messageToChat(msg, me.userId)
}

export type SendMentionMessageResult =
  | {
      status: 'sent'
      message: ChatMessage
      conversationId: string
      switchedToGroup: boolean
    }
  | {
      status: 'existing-group'
      existingGroup: ChatThread
    }

export type CreateGroupResult =
  | { status: 'created'; thread: ChatThread }
  | { status: 'existing'; thread: ChatThread }

export async function sendChatMessageWithMentions(input: {
  conversationId: string
  text: string
  mentionIds: string[]
  channelGroupId: MessageChannelGroupId
  participantIds: string[]
  actorProfile: Pick<UserProfile, 'roleType' | 'providerLevel' | 'displayName' | 'userId'>
  usersById: Map<string, ChatUser>
  replyTo?: ReplyQuote
}): Promise<SendMentionMessageResult> {
  const me = await fetchMe()
  const allMentionIds = [...new Set(input.mentionIds.filter(Boolean))]
  const { humanMentions } = partitionMentions(allMentionIds)
  const uniqueHumanMentions = [...new Set(humanMentions.filter(Boolean))]
  const participantSet = new Set(input.participantIds)
  const newMemberIds = uniqueHumanMentions.filter((id) => !participantSet.has(id))

  for (const id of newMemberIds) {
    if (isBrunoUserId(id)) continue
    if (
      !canMentionToAddMember(
        input.actorProfile,
        input.channelGroupId,
        input.participantIds,
        id,
      )
    ) {
      const name = input.usersById.get(id)?.displayName ?? 'that user'
      throw new Error(`You cannot add ${name} to this conversation`)
    }
  }

  let targetConversationId = input.conversationId
  let switchedToGroup = false

  if (input.channelGroupId === 'direct' && newMemberIds.length > 0) {
    if (!canCreateGroupChat(input.actorProfile)) {
      throw new Error(GROUP_CREATE_FORBIDDEN_MESSAGE)
    }
    const allParticipantIds = buildGroupParticipantSet(
      [...input.participantIds, ...newMemberIds],
      me.userId,
    )
    const existingGroup = await findExistingGroupConversation(allParticipantIds)
    if (existingGroup) {
      return { status: 'existing-group', existingGroup }
    }
    const title = buildGroupTitleFromUsers(allParticipantIds, input.usersById)
    const extraIds = allParticipantIds.filter((id) => id !== me.userId)
    const group = await createConversation({
      type: 'GROUP',
      title,
      participantIds: extraIds,
    })
    targetConversationId = group.conversationId
    switchedToGroup = true
    const addedNames = newMemberIds
      .map((id) => input.usersById.get(id)?.displayName ?? 'someone')
      .join(', ')
    await sendSystemMessage(
      targetConversationId,
      `${input.actorProfile.displayName} started a group chat and added ${addedNames}`,
    )
  } else if (input.channelGroupId === 'groups' && newMemberIds.length > 0) {
    const resultingParticipantIds = buildGroupParticipantSet(
      [...input.participantIds, ...newMemberIds],
      me.userId,
    )
    const existingGroup = await findExistingGroupConversation(
      resultingParticipantIds,
      input.conversationId,
    )
    if (existingGroup) {
      return { status: 'existing-group', existingGroup }
    }
    await updateConversation({
      conversationId: targetConversationId,
      addParticipantIds: newMemberIds,
    })
    for (const id of newMemberIds) {
      const name = input.usersById.get(id)?.displayName ?? 'Someone'
      await sendSystemMessage(
        targetConversationId,
        `${input.actorProfile.displayName} added ${name} to the group`,
      )
    }
  }

  const trimmed = input.text.trim()
  const body = input.replyTo ? encodeReplyBody(input.replyTo, trimmed) : trimmed
  const msg = await sendMessage({
    conversationId: targetConversationId,
    body,
    messageType: 'TEXT',
    mentionIds: allMentionIds.length ? allMentionIds : undefined,
  })

  return {
    status: 'sent',
    message: messageToChat(msg, me.userId),
    conversationId: targetConversationId,
    switchedToGroup,
  }
}

/**
 * Send a GIF (e.g. from GIPHY) as a media message. The GIF bytes are fetched from
 * the provider CDN and uploaded to the chat attachments bucket so sendMessage
 * attachmentUrl validation (S3-only) passes.
 */
export type GifMessageInput = {
  url: string
  description?: string
}

export async function sendChatMessageWithGif(
  conversationId: string,
  gif: GifMessageInput,
  caption?: string,
): Promise<ChatMessage> {
  return sendChatGif(
    conversationId,
    gif.url,
    encodeGifMessageBody(gif.description, caption),
  )
}

export async function sendChatMessageWithAttachment(
  conversationId: string,
  file: File,
  caption?: string,
): Promise<ChatMessage> {
  const me = await fetchMe()
  const contentType = file.type || 'application/octet-stream'
  const attachmentType: AttachmentType = inferAttachmentType(contentType, file.name)
  const result = await uploadChatMedia({
    conversationId,
    file,
    fileName: file.name,
    contentType,
    attachmentType,
    caption: caption?.trim() || undefined,
  })
  return messageToChat(result.message as Message, me.userId)
}

/**
 * Send several images in a single message. The first image is stored in the
 * schema's attachmentUrl/attachmentType fields (so previews + legacy rendering
 * work); the rest are encoded in the message body behind the images marker.
 */
export async function sendChatMessageWithImages(
  conversationId: string,
  files: File[],
  caption?: string,
): Promise<ChatMessage> {
  if (files.length === 0) {
    throw new Error('No images selected')
  }
  if (files.length === 1) {
    return sendChatMessageWithAttachment(conversationId, files[0], caption)
  }

  const me = await fetchMe()

  // Upload in parallel but preserve selection order for the gallery.
  const uploaded = await Promise.all(
    files.map(async (file) => {
      const contentType = file.type || 'application/octet-stream'
      const attachmentType: AttachmentType = inferAttachmentType(contentType, file.name)
      const { attachmentUrl } = await uploadAttachmentFile({
        conversationId,
        file,
        fileName: file.name,
        contentType,
        attachmentType,
      })
      return { attachmentUrl, attachmentType }
    }),
  )

  const [primary, ...rest] = uploaded
  const body = encodeImagesMessageBody(
    rest.map((r) => r.attachmentUrl),
    caption,
  )
  const result = await sendMessageWithAttachment({
    conversationId,
    body,
    attachmentUrl: primary.attachmentUrl,
    attachmentType: primary.attachmentType,
    messageType: 'MEDIA',
  })
  return messageToChat(result as Message, me.userId)
}

export async function markThreadRead(conversationId: string): Promise<void> {
  await markConversationRead(conversationId, new Date().toISOString())
}

export async function sendChatGif(
  conversationId: string,
  gifUrl: string,
  caption?: string,
): Promise<ChatMessage> {
  const me = await fetchMe()
  const result = await uploadGifFromUrl(conversationId, gifUrl, caption?.trim() || undefined)
  const msg = result.message as Message
  return messageToChat(msg, me.userId)
}

export async function createGroupConversation(
  participantIds: string[] = [],
  actorProfile?: Pick<UserProfile, 'roleType' | 'providerLevel'>,
  usersById?: Map<string, ChatUser>,
  customTitle?: string,
): Promise<CreateGroupResult> {
  const me = await fetchMe()
  const profile = actorProfile ?? me
  if (!canCreateGroupChat(profile)) {
    throw new Error(GROUP_CREATE_FORBIDDEN_MESSAGE)
  }
  const allParticipantIds = buildGroupParticipantSet(participantIds, me.userId)
  const existing = await findExistingGroupConversation(allParticipantIds)
  if (existing) {
    return { status: 'existing', thread: existing }
  }
  const trimmedTitle = customTitle?.trim()
  const title =
    trimmedTitle ||
    (usersById
      ? buildGroupTitleFromUsers(allParticipantIds, usersById, 'New group')
      : 'New group')
  const created = await createConversation({
    type: 'GROUP',
    title,
    participantIds,
  })
  return { status: 'created', thread: conversationToThread(created) }
}

export function getDirectThreadTitle(
  thread: ChatThread,
  currentUserId: string,
  usersById: Map<string, ChatUser>,
): string {
  if (isBrunoConversation(thread) && isPersonalBrunoConversation(thread, currentUserId)) {
    return BRUNO_DISPLAY_NAME
  }
  const peerId = (thread.participantIds ?? []).find((id) => id !== currentUserId)
  if (peerId) {
    const peer = usersById.get(peerId)
    if (peer?.displayName) return peer.displayName
  }
  return thread.title
}

/** Prefer a user-renamed group title over auto-generated participant names. */
export function getGroupThreadTitle(
  thread: ChatThread,
  usersById: Map<string, ChatUser>,
): string {
  const explicitTitle = thread.title?.trim()
  const fromMembers = buildGroupTitleFromUsers(thread.participantIds ?? [], usersById)
  if (
    explicitTitle &&
    explicitTitle !== 'Conversation' &&
    explicitTitle !== fromMembers
  ) {
    return explicitTitle
  }
  return fromMembers !== 'Group chat' ? fromMembers : explicitTitle || 'Group chat'
}

export { isBrunoConversation, isPersonalBrunoConversation, partitionMentions }

export function applyReactionToggle(
  currentReactions: ChatMessage['reactions'] = [],
  emoji: string,
  userId: string,
): NonNullable<ChatMessage['reactions']> {
  const reactions = (currentReactions ?? []).map((r) => ({
    emoji: r.emoji,
    userIds: [...r.userIds],
  }))
  const existing = reactions.find((r) => r.emoji === emoji)
  const alreadyReacted = existing?.userIds.includes(userId)

  if (alreadyReacted && existing) {
    existing.userIds = existing.userIds.filter((id) => id !== userId)
    if (existing.userIds.length === 0) {
      return reactions.filter((r) => r.emoji !== emoji)
    }
    return reactions
  }

  if (existing) {
    existing.userIds.push(userId)
  } else {
    reactions.push({ emoji, userIds: [userId] })
  }
  return reactions
}

export async function toggleMessageReaction(
  conversationId: string,
  messageId: string,
  emoji: string,
  userId: string,
  currentReactions: ChatMessage['reactions'] = [],
): Promise<NonNullable<ChatMessage['reactions']>> {
  const existing = currentReactions?.find((r) => r.emoji === emoji)
  const alreadyReacted = existing?.userIds.includes(userId)
  const updated = alreadyReacted
    ? await removeReaction({ conversationId, messageId, emoji })
    : await addReaction({ conversationId, messageId, emoji })
  return (updated.reactions ?? []).map((r) => ({ emoji: r.emoji, userIds: r.userIds }))
}

export async function removeUserFromConversation(
  conversationId: string,
  userId: string,
  actorDisplayName: string,
  userDisplayName: string,
): Promise<void> {
  await updateConversation({
    conversationId,
    removeParticipantIds: [userId],
  })
  await sendSystemMessage(
    conversationId,
    `${actorDisplayName} removed ${userDisplayName} from the group`,
  )
}

export async function editChatMessage(
  conversationId: string,
  messageId: string,
  body: string,
): Promise<ChatMessage> {
  const me = await fetchMe()
  const msg = await updateMessageApi({ conversationId, messageId, body })
  return messageToChat(msg, me.userId)
}

export async function deleteChatMessage(
  conversationId: string,
  messageId: string,
): Promise<ChatMessage> {
  const me = await fetchMe()
  const msg = await deleteMessageApi(conversationId, messageId)
  return messageToChat(msg, me.userId)
}

export async function undeleteChatMessage(
  conversationId: string,
  messageId: string,
): Promise<ChatMessage> {
  const me = await fetchMe()
  const msg = await undeleteMessageApi(conversationId, messageId)
  return messageToChat(msg, me.userId)
}
